import NextLink from 'next/link';
import { colors } from '../../constants/constants';
import { useUmbracoRouteNames } from './UmbracoRouteNamesContext';

export interface BreadcrumbItem {
  href: string;
  label: string;
}

interface BreadcrumbsProps {
  path?: string;
  params?: string[];
  items?: BreadcrumbItem[];
  mb?: number;
}

function getPathSegments(path?: string, params?: string[]): string[] {
  const resolvedPath = path ?? (params?.length ? `/${params.join('/')}` : '');

  return resolvedPath.split(/[?#]/)[0].split('/').filter(Boolean);
}

function toLabel(segment: string): string {
  return decodeURIComponent(segment)
    .replace(/-/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalizePath(path: string): string {
  return (path || '').replace(/^\/+|\/+$/g, '');
}

function getRouteLabel(
  routeNameByPath: Map<string, string>,
  href: string,
  fallbackSegment: string,
): string {
  return routeNameByPath.get(normalizePath(href)) ?? toLabel(fallbackSegment);
}

function formatStorageUnitLabel(segment: string, label: string): string {
  const storageUnitMatch = segment.match(
    /^(\d+)(?:-(\d+))?-m2-(forrad|garage)$/i,
  );

  if (!storageUnitMatch) {
    return label.replace('Forrad', 'Förråd');
  }

  const size = storageUnitMatch[2]
    ? `${storageUnitMatch[1]},${storageUnitMatch[2]}`
    : storageUnitMatch[1];
  const productType =
    storageUnitMatch[3].toLowerCase() === 'forrad' ? 'förråd' : 'garage';

  return `${size} m2 ${productType}`;
}

function renderCrumbs(crumbs: BreadcrumbItem[], mb: number): JSX.Element {
  return (
    <div
      style={{
        maxWidth: '80rem',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingTop: '1.2rem',
        paddingBottom: '0.5rem',
      }}>
      <nav aria-label="Breadcrumb">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: `${mb * 0.25}rem`,
            fontSize: '1rem',
            color: colors.neutrals[900],
          }}>
          <span>
            <NextLink
              href="/"
              style={{ color: 'inherit', textDecoration: 'none' }}>
              Mitt Lager
            </NextLink>
          </span>

          {crumbs.map((item, index) => (
            <span key={item.href}>
              <span style={{ marginRight: 8 }}>|</span>
              {index === crumbs.length - 1 ? (
                <span style={{ fontWeight: 'bold' }}>{item.label}</span>
              ) : (
                <NextLink
                  href={item.href}
                  style={{
                    color: 'inherit',
                    textDecoration: 'none',
                    fontWeight: 'normal',
                  }}>
                  {item.label}
                </NextLink>
              )}
            </span>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default function Breadcrumbs({
  path,
  params,
  items,
  mb = 4,
}: BreadcrumbsProps = {}): JSX.Element | null {
  const routeNames = useUmbracoRouteNames();

  //IF CUSTOM BREADCRUMBS ARE PROVIDED, USE THEM INSTEAD OF GENERATING FROM PATH
  if (items?.length) {
    return renderCrumbs(items, mb);
  }

  const segments = getPathSegments(path, params);

  if (!segments.length) {
    return null;
  }

  const crumbSegments = segments;
  const routeNameByPath = new Map<string, string>(
    routeNames.map((route) => [normalizePath(route.path), route.name]),
  );

  const generatedCrumbs = crumbSegments.map((segment, index) => {
    const href = `/${crumbSegments.slice(0, index + 1).join('/')}`;
    const label = formatStorageUnitLabel(
      segment,
      getRouteLabel(routeNameByPath, href, segment),
    );

    return { href, label };
  });

  //IF URL PATH IS PROVIDED, GENERATE BREADCRUMBS BASED ON THAT PATH
  return renderCrumbs(generatedCrumbs, mb);
}
